import Duck from 'extensible-duck';
import Immutable from 'immutable';
import { schema, denormalize, normalize } from 'normalizr';

import requestsManagerAxios from '../../lib/requestsManagerAxios';

const announcementAttachmentSchema = new schema.Entity(
  '$$announcementAttachments'
);
const userSchema = new schema.Entity('$$users');
const commentSchema = new schema.Entity('$$comments', {
  user: userSchema,
});
export const announcementSchema = new schema.Entity('$$announcements', {
  comments: [commentSchema],
  attachments: [announcementAttachmentSchema],
});

export default new Duck({
  namespace: 'public',
  store: 'announcement',
  types: [
    'RESET_ANNOUNCEMENTS',
    'CREATE_COMMENT',
    'CREATE_COMMENT_PENDING',
    'CREATE_COMMENT_REJECTED',
    'CREATE_COMMENT_FULFILLED',
    'DELETE_ANNOUNCEMENT',
    'DELETE_ANNOUNCEMENT_PENDING',
    'DELETE_ANNOUNCEMENT_REJECTED',
    'DELETE_ANNOUNCEMENT_FULFILLED',
    'DELETE_COMMENT',
    'DELETE_COMMENT_PENDING',
    'DELETE_COMMENT_REJECTED',
    'DELETE_COMMENT_FULFILLED',
    'FETCH_ANNOUNCEMENTS',
    'FETCH_ANNOUNCEMENTS_PENDING',
    'FETCH_ANNOUNCEMENTS_REJECTED',
    'FETCH_ANNOUNCEMENTS_FULFILLED',
    'UPDATE_ANNOUNCEMENT',
    'UPDATE_ANNOUNCEMENT_PENDING',
    'UPDATE_ANNOUNCEMENT_REJECTED',
    'UPDATE_ANNOUNCEMENT_FULFILLED',
    'UPDATE_COMMENT',
    'UPDATE_COMMENT_PENDING',
    'UPDATE_COMMENT_REJECTED',
    'UPDATE_COMMENT_FULFILLED',
  ],
  initialState: Immutable.fromJS({
    $$announcements: [],
    $$entities: {},
    announcementId: null,
    currentPage: 0,
    isLastPage: false,
    isFetching: false,
    isSaving: false,
  }),
  reducer: ($$state, { type, payload, meta }, { types }) => {
    switch (type) {
      case 'public/organization/CREATE_EXAMPLE_ANNOUNCEMENT_FULFILLED':
        return $$state.set('$$announcements', payload.data.announcements);
      case types.CREATE_COMMENT_PENDING:
        return $$state.merge({ isSaving: true });
      case types.CREATE_COMMENT_REJECTED:
        if (payload.response.status === 401) {
          window.location = payload.response.headers.location;
          return $$state;
        }
        return $$state.merge({ isSaving: false, storedCommentText: '' });
      case types.CREATE_COMMENT_FULFILLED: {
        const normalized = normalize(payload.data, commentSchema);
        return $$state
          .mergeDeep(
            Immutable.fromJS({
              $$entities: normalized.entities,
              isSaving: false,
              storedCommentText: '',
            })
          )
          .updateIn(
            [
              '$$entities',
              '$$announcements',
              $$state.get('announcementId').toString(),
              'comments',
            ],
            $$commentIds => $$commentIds.unshift(payload.data.id)
          );
      }
      case types.DELETE_ANNOUNCEMENT_PENDING:
        return $$state.merge({ isSaving: true });
      case types.DELETE_ANNOUNCEMENT_REJECTED:
        return $$state.merge({ isSaving: false });
      case types.DELETE_ANNOUNCEMENT_FULFILLED:
        return $$state
          .merge({ isSaving: false })
          .update('$$announcements', $$announcements =>
            $$announcements.filterNot(
              $$announcement => $$announcement.id === meta.id
            )
          );
      case types.DELETE_COMMENT_PENDING:
        return $$state.merge({ isSaving: true });
      case types.DELETE_COMMENT_REJECTED:
        return $$state.merge({ isSaving: false });
      case types.DELETE_COMMENT_FULFILLED:
        return $$state
          .merge({ isSaving: false })
          .updateIn(
            [
              '$$entities',
              '$$announcements',
              $$state.get('announcementId').toString(),
              'comments',
            ],
            $$comments =>
              $$comments.filterNot(commentId => meta.commentId === commentId)
          );
      case types.FETCH_ANNOUNCEMENTS_PENDING:
        return $$state.merge({ isFetching: true });
      case types.FETCH_ANNOUNCEMENTS_REJECTED:
        return $$state.merge({ isFetching: false, isLastPage: true });
      case types.FETCH_ANNOUNCEMENTS_FULFILLED: {
        const { currentPage, isLastPage, announcements } = payload.data;
        return $$state
          .merge({ isFetching: false, currentPage, isLastPage })
          .update('$$announcements', $$announcements =>
            $$announcements.concat(Immutable.List(announcements))
          );
      }
      case types.RESET_ANNOUNCEMENTS:
        return $$state.merge({ $$announcements: Immutable.List() });
      case types.UPDATE_ANNOUNCEMENT_PENDING:
        return $$state.merge({ isSaving: true });
      case types.UPDATE_ANNOUNCEMENT_REJECTED:
        return $$state.merge({ isSaving: false });
      case types.UPDATE_ANNOUNCEMENT_FULFILLED:
        return $$state
          .merge({ isSaving: false })
          .setIn(
            ['$$entities', '$$announcements', payload.data.id.toString()],
            payload.data
          );
      case types.UPDATE_COMMENT_PENDING:
        return $$state.merge({ isSaving: true });
      case types.UPDATE_COMMENT_REJECTED:
        return $$state.merge({ isSaving: false });
      case types.UPDATE_COMMENT_FULFILLED: {
        const normalized = normalize(payload.data, commentSchema);
        return $$state.mergeDeep({
          $$entities: Immutable.fromJS(normalized.entities),
          isSaving: false,
        });
      }
      default:
        return $$state;
    }
  },
  selectors: {
    $$announcements: $$state => $$state.get('$$announcements'),
    $$announcement: $$state => {
      const input = $$state.get('announcementId');
      const entities = $$state.get('$$entities');
      return denormalize(input, announcementSchema, entities);
    },
  },
  creators: ({ types }) => ({
    createComment: (commentThreadId, comment) => ({
      type: types.CREATE_COMMENT,
      payload: requestsManagerAxios.post(
        `/user/comment_threads/${commentThreadId}/comments`,
        { comment: { comment } }
      ),
    }),
    deleteComment: commentId => ({
      type: types.DELETE_COMMENT,
      payload: requestsManagerAxios.delete(`/user/comments/${commentId}`),
      meta: { commentId },
    }),
    fetchAnnouncements: () => (dispatch, getState) => {
      const { $$announcementStore } = getState();
      const nextPage = $$announcementStore.get('currentPage') + 1;
      return dispatch({
        type: types.FETCH_ANNOUNCEMENTS,
        payload: requestsManagerAxios.get(
          `/announcements.json?page=${nextPage}`
        ),
      });
    },
    deleteAnnouncement: id => ({
      type: types.DELETE_ANNOUNCEMENT,
      payload: requestsManagerAxios.delete(`/admin/announcements/${id}`),
      meta: { id },
    }),
    updateAnnouncement: (id, announcement) => ({
      type: types.UPDATE_ANNOUNCEMENT,
      payload: requestsManagerAxios.patch(`/admin/announcements/${id}`, {
        announcement,
      }),
    }),
    updateComment: (commentId, comment) => ({
      type: types.UPDATE_COMMENT,
      payload: requestsManagerAxios.patch(`/user/comments/${commentId}`, {
        comment: { comment },
      }),
    }),
  }),
});
